import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { now as tzNow } from './timezone'

dayjs.extend(isoWeek)

// ⚠️ 這支所有的「現在」都要用 tzNow()（店家時區），不能用 dayjs()。
// 用 dayjs() 的話，店家人在清邁（UTC+7）的 23:00–24:00 之間，
// 台灣已經是隔天了，「今日」卻會抓到昨天的預約，而且提示列還寫著「今日 9/15」。

/* ════════════════════════════════════════════════════════
   預約管理頁的「進站預設區間」
   ────────────────────────────────────────────────────────
   店家反應一進預約管理看到的是「全部預約」，每天都要自己再按一次
   今日／本週才看得到當班的單。這裡記住他最後用過的那一個，
   下次進來直接套用；沒有記錄時（新使用者、換裝置）預設「今日」。

   存 localStorage 而不是後端設定：這是「這台機器上的這個人習慣怎麼看」，
   櫃台與老闆共用同一組帳號的店家也能各自記各自的。
   讀寫都包 try/catch —— 無痕模式與擋儲存的瀏覽器會直接丟例外，
   不能讓一個檢視偏好把整頁弄掛。
   ════════════════════════════════════════════════════════ */

const KEY = 'bookingListRangePreset'

/** @returns {'today'|'week'|'all'|null} null = 沒有記錄過 */
export const readBookingRangePreset = () => {
  try {
    const v = window.localStorage.getItem(KEY)
    return v === 'today' || v === 'week' || v === 'all' ? v : null
  } catch (e) {
    return null
  }
}

export const writeBookingRangePreset = (preset) => {
  try {
    window.localStorage.setItem(KEY, preset)
  } catch (e) {
    /* 存不進去只是少了記憶，不影響操作 */
  }
}

/**
 * preset → [start, end]。
 * 'all'（或無效值）回 null，代表不限日期，走原本的「全部預約」。
 * 週一～週日（isoWeek），跟快捷鍵與後端的「本週預約」統計一致。
 */
export const bookingRangeOfPreset = (preset) => {
  const now = tzNow()
  if (preset === 'today') return [now.startOf('day'), now.endOf('day')]
  if (preset === 'week') return [now.startOf('isoWeek'), now.endOf('isoWeek')]
  return null
}

/** dateRange 可能是 moment 也可能是 dayjs（兩條路徑各建各的），一律用字串比 */
const toDay = (v) =>
  v && typeof v.format === 'function' ? v.format('YYYY-MM-DD') : null

/** 目前的 dateRange 對應到哪一顆快捷鍵；不是今日／本週就回 null */
export const presetOfBookingRange = (range) => {
  const start = toDay(range?.[0])
  const end = toDay(range?.[1])
  if (!start || !end) return null
  const now = tzNow()
  if (start === now.format('YYYY-MM-DD') && end === now.format('YYYY-MM-DD')) {
    return 'today'
  }
  if (
    start === now.startOf('isoWeek').format('YYYY-MM-DD') &&
    end === now.endOf('isoWeek').format('YYYY-MM-DD')
  ) {
    return 'week'
  }
  return null
}

/** 給提示列用的可讀標籤，例如「今日 9/9」「本週 9/6–9/12」「9/1–9/30」 */
export const formatBookingRangeLabel = (range, preset) => {
  const now = tzNow()
  if (preset === 'today') return `今日 ${now.format('M/D')}`
  if (preset === 'week') {
    return `本週 ${now.startOf('isoWeek').format('M/D')}–${now
      .endOf('isoWeek')
      .format('M/D')}`
  }
  const fmt = (v) => (v && typeof v.format === 'function' ? v.format('M/D') : null)
  const s = fmt(range?.[0])
  const e = fmt(range?.[1])
  if (!s || !e) return null
  return s === e ? s : `${s}–${e}`
}
